/**
 * Convert backend datetime string to ISO format with UTC timezone
 * @param {string} backendDateTime - DateTime string from backend (format: "YYYY-MM-DD HH:mm:ss")
 * @returns {string} ISO format datetime string with Z (UTC)
 */
export const convertToISOFormat = (backendDateTime) => {
  if (!backendDateTime) return null

  try {
    // Convert backend format to ISO format with Z (UTC)
    const isoFormat = backendDateTime.replace(' ', 'T') + 'Z'
    return isoFormat
  } catch (error) {
    console.error('Error converting to ISO format:', error)
    return null
  }
}

/**
 * Convert backend datetime to user's local timezone
 * @param {string} backendDateTime - DateTime string from backend (format: "YYYY-MM-DD HH:mm:ss")
 * @returns {Date|null} Date object in local timezone
 */
export const convertToLocalDateTime = (backendDateTime) => {
  if (!backendDateTime) return null

  try {
    const isoFormat = convertToISOFormat(backendDateTime)
    if (!isoFormat) return null

    // Create Date object (automatically converts to local timezone)
    const localDateTime = new Date(isoFormat)

    // Check if date is valid
    if (isNaN(localDateTime.getTime())) {
      console.error('Invalid date:', backendDateTime)
      return null
    }

    return localDateTime
  } catch (error) {
    console.error('Error converting to local datetime:', error)
    return null
  }
}

/**
 * Format backend datetime to user's local timezone string
 * @param {string} backendDateTime - DateTime string from backend (format: "YYYY-MM-DD HH:mm:ss")
 * @param {object} options - Intl.DateTimeFormat options (optional)
 * @param {boolean} includeSeconds - Whether to include seconds in the output (default: false)
 * @returns {string} Formatted datetime string in local timezone
 */
export const formatToLocalDateTime = (backendDateTime, options = {}, includeSeconds = false) => {
  if (!backendDateTime) return "N/A"

  try {
    const localDateTime = convertToLocalDateTime(backendDateTime)
    if (!localDateTime) return backendDateTime

    // Default format options
    const defaultOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }

    // Add seconds if requested
    if (includeSeconds) {
      defaultOptions.second = '2-digit'
    }

    // Merge with custom options if provided
    const formatOptions = { ...defaultOptions, ...options }

    // Format to user's local timezone
    const formattedDateTime = localDateTime.toLocaleString('en-GB', formatOptions)
    return formattedDateTime
  } catch (error) {
    console.error('Error formatting datetime:', error)
    return backendDateTime
  }
}

/**
 * Format backend date to user's local date string (without time)
 * @param {string} backendDateTime - DateTime string from backend
 * @returns {string} Formatted date string in local timezone (e.g., "9 Jan 2026")
 */
export const formatToLocalDate = (backendDateTime) => {
  if (!backendDateTime) return "N/A"

  try {
    const localDateTime = convertToLocalDateTime(backendDateTime)
    if (!localDateTime) return backendDateTime

    return localDateTime.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return backendDateTime
  }
}

/**
 * Format backend time to user's local time string (without date)
 * @param {string} backendDateTime - DateTime string from backend
 * @returns {string} Formatted time string in local timezone (e.g., "14:30")
 */
export const formatToLocalTime = (backendDateTime) => {
  if (!backendDateTime) return "N/A"

  try {
    const localDateTime = convertToLocalDateTime(backendDateTime)
    if (!localDateTime) return backendDateTime

    return localDateTime.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Error formatting time:', error)
    return backendDateTime
  }
}


export const convertLocalToUTC = (localDateTime) => {
  if (!localDateTime) return ''

  try {
    const localDate = new Date(localDateTime)

    // Check if date is valid
    if (isNaN(localDate.getTime())) {
      console.error('Invalid date:', localDateTime)
      return ''
    }

    return localDate.toISOString()
      .replace('T', ' ')           // Replace T with space
      .replace(/\.\d{3}Z$/, '')    // Remove milliseconds and Z
  } catch (error) {
    console.error('Error converting local datetime to UTC:', error)
    return ''
  }
}
